/**
 * ScheduleToolbar — composable toolbar for the schedule surface.
 *
 * A compound component. The shared frame lays out three regions — a left slot,
 * the centered Practitioners/Resources mode tabs, and a right slot — so each
 * view (practitioner calendar, resource timeline) composes its own left/right
 * controls around one consistent frame + mode picker.
 *
 * State is dependency-injected: `Root` receives the current `mode` and an
 * `onModeChange` handler and exposes them via context. The toolbar primitives
 * never reach into the schedule store themselves, so they stay reusable and
 * testable — the consumer decides where `mode` comes from.
 *
 *   <ScheduleToolbar.Root mode={mode} onModeChange={setMode} className="p-3">
 *     <ScheduleToolbar.Left>{…}</ScheduleToolbar.Left>
 *     <ScheduleToolbar.ModeTabs />
 *     <ScheduleToolbar.Right>{…}</ScheduleToolbar.Right>
 *   </ScheduleToolbar.Root>
 */

import { Tabs, TabsList, TabsTrigger } from "@repo/ui/components/ui/tabs";
import { cn } from "@repo/ui/lib/utils";
import { createContext, use } from "react";
import type { TimelineMode } from "@/features/scheduling/stores/schedule-store";

interface ScheduleToolbarContextValue {
  mode: TimelineMode;
  setMode: (mode: TimelineMode) => void;
}

const ScheduleToolbarContext = createContext<ScheduleToolbarContextValue | null>(null);

function useScheduleToolbar(): ScheduleToolbarContextValue {
  const ctx = use(ScheduleToolbarContext);
  if (!ctx) {
    throw new Error("ScheduleToolbar.* must be rendered within <ScheduleToolbar.Root>");
  }
  return ctx;
}

function ScheduleToolbarRoot({
  mode,
  onModeChange,
  className,
  children,
}: {
  mode: TimelineMode;
  onModeChange: (mode: TimelineMode) => void;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <ScheduleToolbarContext value={{ mode, setMode: onModeChange }}>
      {/* 3-column grid: left column left-aligned, center column truly centered,
          right column right-aligned — independent of the side widths. */}
      <div className={cn("grid grid-cols-[1fr_auto_1fr] items-center gap-2", className)}>{children}</div>
    </ScheduleToolbarContext>
  );
}

function ScheduleToolbarLeft({ children }: { children: React.ReactNode }) {
  return <div className="flex items-center gap-2 justify-self-start">{children}</div>;
}

function ScheduleToolbarRight({ children }: { children: React.ReactNode }) {
  return <div className="flex items-center justify-end gap-2 justify-self-end">{children}</div>;
}

const MODE_OPTIONS: { value: TimelineMode; label: string }[] = [
  { value: "practitioners", label: "Practitioners" },
  { value: "resources", label: "Resources" },
];

/**
 * Practitioners | Resources segmented tabs, placed in the grid's center column
 * (justify-self-center) so it sits at the toolbar's true midpoint.
 */
function ScheduleToolbarModeTabs() {
  const { mode, setMode } = useScheduleToolbar();
  return (
    <Tabs value={mode} onValueChange={(value) => setMode(value as TimelineMode)} className="justify-self-center">
      <TabsList>
        {MODE_OPTIONS.map(({ value, label }) => (
          <TabsTrigger key={value} value={value} className="px-3.5 text-xs">
            {label}
          </TabsTrigger>
        ))}
      </TabsList>
    </Tabs>
  );
}

export const ScheduleToolbar = {
  Root: ScheduleToolbarRoot,
  Left: ScheduleToolbarLeft,
  ModeTabs: ScheduleToolbarModeTabs,
  Right: ScheduleToolbarRight,
};
